import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import GoogleAddressInput from "@/components/GoogleAddressInput";
import { ShieldCheck, ArrowRight, ArrowLeft, MapPin, CheckCircle2, CreditCard, Check } from "lucide-react";
import { fmtMoney } from "@/lib/format";
import { toast } from "sonner";
import { company } from "@/lib/branding";

const STEPS = ["Address", "Service", "Account", "Review"];

// Fallback shown if the API call is in flight or fails. Mirrors the
// server's DEFAULT_SIGNUP_FORM so the wizard still renders in isolation.
const FALLBACK_CONFIG = {
    cart_sizes: [{ value: "96gal", label: "96 gallon" }],
    service_types: [
        { id: "residential", label: "Residential",
          description: "Curbside cart pickup",
          enabled: true, quote_only: false },
        { id: "commercial",
          label: "Residential / Commercial Dumpster Service",
          description: "Dumpster service — we'll follow up with a quote",
          enabled: true, quote_only: true },
        { id: "rolloff", label: "Roll-off",
          description: "Construction & clean-out containers",
          enabled: true, quote_only: true },
    ],
    plans: {
        residential: [
            { id: "weekly_1cart", label: "1 cart weekly", desc: "Standard 96gal residential", rate: 96 },
            { id: "weekly_2cart", label: "2 carts weekly", desc: "Two 96gal carts", rate: 135 },
            { id: "weekly_3cart", label: "3 carts weekly", desc: "Three 96gal carts", rate: 165 },
            { id: "weekly_4cart", label: "4 carts weekly", desc: "Four 96gal carts", rate: 200 },
        ],
        commercial: [],
        rolloff: [],
    },
};

export default function SignupWizard() {
    const [step, setStep] = useState(0);
    const [busy, setBusy] = useState(false);
    const [areaCheck, setAreaCheck] = useState(null);
    const [mapsState, setMapsState] = useState("loading"); // loading | ready | error
    const [config, setConfig] = useState(FALLBACK_CONFIG);
    const nav = useNavigate();
    const [data, setData] = useState({
        address: { line1: "", line2: "", city: "", state: "", zip: "" },
        service_type: "residential",
        plan: "weekly_1cart",
        monthly_rate: 96,
        cart_size: "96gal",
        name: "",
        email: "",
        phone: "",
        accept_terms: false,
    });

    // Pull the admin-editable signup config (cart sizes / plans / labels).
    useEffect(() => {
        api.get("/public/signup-form-config")
            .then(({ data: cfg }) => {
                setConfig(cfg);
                // Seed defaults from the freshly-loaded config so the
                // first render uses the admin's cart size + plan.
                setData((d) => {
                    const firstCart = cfg.cart_sizes?.[0]?.value || d.cart_size;
                    const firstST = cfg.service_types?.find((s) => s.enabled)?.id || d.service_type;
                    const firstPlan = cfg.plans?.[firstST]?.[0];
                    return {
                        ...d,
                        cart_size: firstCart,
                        service_type: firstST,
                        plan: firstPlan?.id || "quote",
                        monthly_rate: firstPlan?.rate || 0,
                    };
                });
            })
            .catch(() => { /* fallback config stays */ });
    }, []);

    // Derived: enabled service types + plans for the current selection.
    const enabledTypes = (config.service_types || []).filter((s) => s.enabled);
    const currentType = enabledTypes.find((s) => s.id === data.service_type)
                          || enabledTypes[0];
    const plans = (config.plans?.[currentType?.id] || []);
    const isQuoteOnly = !!currentType?.quote_only;

    const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
    const setAddr = (k, v) => setData((d) => ({ ...d, address: { ...d.address, [k]: v } }));

    const checkArea = async () => {
        // Uses the actual drawn service-area circles set up under /service-area
        // instead of a hard-coded ZIP list. Prefers lat/lng captured from a
        // Google prediction (most accurate); falls back to geocoding the
        // typed street+city+state+zip on the server.
        const { line1, line2, city, state, zip, lat, lng } = data.address;
        if (lat != null && lng != null) {
            // Nothing to validate — we already have coords.
        } else if (!line1 || !city || !state) {
            return toast.error("Enter street, city, and state");
        }
        try {
            const params = new URLSearchParams();
            if (lat != null && lng != null) {
                params.set("lat", String(lat));
                params.set("lng", String(lng));
            } else {
                const parts = [line1, line2, city, state, zip].filter(Boolean);
                params.set("address", parts.join(", "));
            }
            const { data: r } = await api.get(`/public/service-area/check?${params.toString()}`);
            // Adapt the response shape to what the badge below expects.
            if (r.configured === false) {
                // No service area drawn — treat as in-area.
                setAreaCheck({ in_service_area: true, message: r.message });
                toast.success("Service area not configured — proceed");
                return;
            }
            if (r.in_area === null) {
                setAreaCheck({ in_service_area: null,
                                message: r.message || "Could not verify address" });
                toast.error(r.message || "Could not verify address");
                return;
            }
            setAreaCheck({
                in_service_area: !!r.in_area,
                area_name: r.area_name,
            });
            if (r.in_area) toast.success(`You're in our service area${r.area_name ? ` — ${r.area_name}` : ""}`);
            else toast.error("Outside service area — our team will follow up");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not verify");
        }
    };

    const submit = async () => {
        if (!data.accept_terms) return toast.error("Please accept terms");
        setBusy(true);
        try {
            // Quote-only services (dumpster, roll-off) create a lead and
            // stop — no Stripe checkout because we haven't priced the job yet.
            if (isQuoteOnly) {
                await api.post("/public/signup", {
                    name: data.name,
                    email: data.email,
                    phone: data.phone,
                    line1: data.address.line1,
                    line2: data.address.line2 || null,
                    city: data.address.city,
                    state: data.address.state,
                    zip: data.address.zip,
                    cart_size: data.cart_size,
                    service_type: data.service_type,
                    notes: `Quote request — ${currentType?.label || data.service_type}`,
                });
                toast.success("Quote request received — we'll follow up within one business day");
                nav("/signup/thanks?type=quote");
                return;
            }

            // Standard residential flow — creates the customer, invoices,
            // and hands off to Stripe for the first payment.
            const { data: result } = await api.post("/signup", {
                name: data.name, email: data.email, phone: data.phone,
                address: data.address, service_type: data.service_type,
                cart_size: data.cart_size, plan: data.plan,
                monthly_rate: data.monthly_rate, accept_terms: true,
            });
            toast.success("Account created — redirecting to payment");
            const origin = window.location.origin;
            const { data: pay } = await api.post(`/payments/checkout/invoice/${result.invoice_id}`,
                { origin_url: origin });
            window.location.href = pay.url;
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Signup failed");
            setBusy(false);
        }
    };

    // (plans + isQuoteOnly + enabledTypes already computed above from config)

    return (
        <div className="min-h-screen bg-background" data-testid="signup-wizard">
            <header className="border-b border-border">
                <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center">
                            <ShieldCheck className="w-4 h-4 text-primary-foreground" />
                        </div>
                        <div>
                            <div className="font-heading font-bold text-sm">{company()}</div>
                            <div className="overline text-[9px]">Sign up for service</div>
                        </div>
                    </Link>
                    <Link to="/login" className="text-xs text-muted-foreground hover:text-foreground">Staff login →</Link>
                </div>
            </header>

            <div className="max-w-3xl mx-auto px-6 py-12">
                {/* Step indicator */}
                <div className="flex items-center justify-between mb-12">
                    {STEPS.map((label, i) => (
                        <div key={label} className="flex items-center flex-1 last:flex-initial" data-testid={`step-indicator-${i}`}>
                            <div className={`flex items-center gap-3 ${i === step ? "text-foreground" : "text-muted-foreground"}`}>
                                <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-bold tabular ${
                                    i < step ? "bg-primary border-primary text-primary-foreground" : i === step ? "border-primary text-primary" : "border-border"
                                }`}>
                                    {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
                                </div>
                                <span className="text-xs uppercase tracking-wider font-medium hidden sm:inline">{label}</span>
                            </div>
                            {i < STEPS.length - 1 && (
                                <div className={`h-px flex-1 mx-3 ${i < step ? "bg-primary" : "bg-border"}`} />
                            )}
                        </div>
                    ))}
                </div>

                <Card>
                    <CardContent className="p-8">
                        {step === 0 && (
                            <div className="space-y-5" data-testid="step-address">
                                <div>
                                    <div className="overline mb-2">Step 1</div>
                                    <h2 className="font-heading text-3xl font-bold tracking-tight">Where is your service location?</h2>
                                    <p className="text-sm text-muted-foreground mt-2">We'll verify availability in your area before continuing.</p>
                                </div>
                                <div className="space-y-3">
                                    <div>
                                        <Label className="text-xs">Search your address</Label>
                                        <GoogleAddressInput
                                            value={data.address.line1}
                                            onChange={(v) => setAddr("line1", v)}
                                            onLoadStateChange={setMapsState}
                                            onAddressSelected={(parsed) => {
                                                setData((d) => ({
                                                    ...d,
                                                    address: {
                                                        ...d.address,
                                                        line1: parsed.line1 || d.address.line1,
                                                        city: parsed.city || d.address.city,
                                                        state: parsed.state || d.address.state,
                                                        zip: parsed.zip || d.address.zip,
                                                        lat: parsed.lat,
                                                        lng: parsed.lng,
                                                    },
                                                }));
                                                setAreaCheck(null);
                                            }}
                                            testid="addr-line1"
                                        />
                                        <div className="text-[11px] text-muted-foreground mt-1">
                                            {mapsState === "error"
                                                ? "Type your street address, city, state, and ZIP below — we'll verify service using your ZIP code."
                                                : "Powered by Google — pick your address from the list, or type manually."}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div><Label className="text-xs">City</Label>
                                            <Input value={data.address.city} onChange={(e) => setAddr("city", e.target.value)} data-testid="addr-city" /></div>
                                        <div><Label className="text-xs">State</Label>
                                            <Input value={data.address.state} onChange={(e) => setAddr("state", e.target.value)} data-testid="addr-state" /></div>
                                    </div>
                                    <div className="flex gap-3 items-end">
                                        <div className="flex-1"><Label className="text-xs">ZIP code</Label>
                                            <Input value={data.address.zip} onChange={(e) => setAddr("zip", e.target.value)} data-testid="addr-zip" /></div>
                                        <Button variant="outline" onClick={checkArea} data-testid="check-area-btn">
                                            <MapPin className="w-4 h-4 mr-1" />Verify service area
                                        </Button>
                                    </div>
                                    {areaCheck && (
                                        <div data-testid="area-check-result" className={`p-3 rounded-md text-sm border ${
                                            areaCheck.in_service_area ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                                                                       : "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400"
                                        }`}>
                                            {areaCheck.in_service_area
                                                ? <><CheckCircle2 className="w-4 h-4 inline mr-1" />You're in our service area. Continue below.</>
                                                : <>Outside our current service area. Submit anyway and our team will follow up.</>}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {step === 1 && (
                            <div className="space-y-5" data-testid="step-service">
                                <div>
                                    <div className="overline mb-2">Step 2</div>
                                    <h2 className="font-heading text-3xl font-bold tracking-tight">What service do you need?</h2>
                                </div>
                                <div className={`grid grid-cols-${Math.max(1, Math.min(enabledTypes.length, 3))} gap-3`}>
                                    {enabledTypes.map((st) => (
                                        <button
                                            key={st.id}
                                            onClick={() => {
                                                set("service_type", st.id);
                                                const first = (config.plans?.[st.id] || [])[0];
                                                set("plan", first?.id || "quote");
                                                set("monthly_rate", first?.rate || 0);
                                            }}
                                            data-testid={`service-${st.id}`}
                                            className={`p-4 rounded-md border-2 text-left transition-all ${
                                                data.service_type === st.id
                                                    ? "border-primary bg-primary/5"
                                                    : "border-border hover:border-foreground/30"
                                            }`}
                                        >
                                            <div className="font-heading font-semibold">{st.label}</div>
                                            <div className="text-xs text-muted-foreground mt-1">
                                                {st.description}
                                            </div>
                                            {st.quote_only && (
                                                <div className="mt-2 inline-block text-[10px] uppercase tracking-wide bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                                                    Quote required
                                                </div>
                                            )}
                                        </button>
                                    ))}
                                </div>

                                {isQuoteOnly ? (
                                    <div className="border-2 border-dashed border-border rounded-md p-6 space-y-2" data-testid="quote-request-box">
                                        <div className="font-heading text-lg font-semibold">Request a quote</div>
                                        <p className="text-sm text-muted-foreground">
                                            We'll follow up within one business day with pricing and
                                            availability. No charge until you approve the quote.
                                        </p>
                                        <div className="text-xs text-muted-foreground">
                                            Finish the rest of this form so we know where to deliver and how to reach you.
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        <Label className="text-xs">Choose a plan</Label>
                                        {plans.map((p) => (
                                            <button
                                                key={p.id}
                                                onClick={() => { set("plan", p.id); set("monthly_rate", p.rate); }}
                                                data-testid={`plan-${p.id}`}
                                                className={`w-full text-left p-4 border rounded-md transition-all flex items-center justify-between ${
                                                    data.plan === p.id ? "border-primary bg-primary/5" : "border-border hover:border-foreground/30"
                                                }`}
                                            >
                                                <div>
                                                    <div className="font-medium">{p.label}</div>
                                                    <div className="text-xs text-muted-foreground">{p.desc}</div>
                                                </div>
                                                <div className="font-heading text-lg font-bold tabular">{p.rate ? `${fmtMoney(p.rate)}/qtr` : "Quote"}</div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {data.service_type === "residential" && !isQuoteOnly && (config.cart_sizes?.length > 1) && (
                                    <div className="space-y-1.5">
                                        <Label className="text-xs">Cart size</Label>
                                        <Select value={data.cart_size} onValueChange={(v) => set("cart_size", v)}>
                                            <SelectTrigger data-testid="cart-size"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {config.cart_sizes.map((c) => (
                                                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                            </div>
                        )}

                        {step === 2 && (
                            <div className="space-y-5" data-testid="step-account">
                                <div>
                                    <div className="overline mb-2">Step 3</div>
                                    <h2 className="font-heading text-3xl font-bold tracking-tight">Create your account</h2>
                                </div>
                                <div className="space-y-3">
                                    <div><Label className="text-xs">Full name</Label>
                                        <Input value={data.name} onChange={(e) => set("name", e.target.value)} data-testid="acct-name" /></div>
                                    <div><Label className="text-xs">Email</Label>
                                        <Input type="email" value={data.email} onChange={(e) => set("email", e.target.value)} data-testid="acct-email" /></div>
                                    <div><Label className="text-xs">Phone</Label>
                                        <Input value={data.phone} onChange={(e) => set("phone", e.target.value)} data-testid="acct-phone" /></div>
                                </div>
                            </div>
                        )}

                        {step === 3 && (
                            <div className="space-y-5" data-testid="step-review">
                                <div>
                                    <div className="overline mb-2">Step 4</div>
                                    <h2 className="font-heading text-3xl font-bold tracking-tight">
                                        {isQuoteOnly ? "Review and request quote" : "Review and pay first invoice"}
                                    </h2>
                                    <p className="text-sm text-muted-foreground mt-2">
                                        {isQuoteOnly
                                            ? "We'll get back to you within one business day with pricing and availability."
                                            : "You'll be redirected to Stripe to complete payment."}
                                    </p>
                                </div>
                                <div className="border border-border rounded-md p-5 space-y-3">
                                    <Row k="Service location" v={`${data.address.line1}, ${data.address.city} ${data.address.zip}`} />
                                    <Row k="Service type" v={currentType?.label || data.service_type} />
                                    {!isQuoteOnly && (
                                        <Row k="Plan" v={`${data.plan.replace(/_/g, " ")} · ${fmtMoney(data.monthly_rate)}/qtr`} />
                                    )}
                                    <Row k="Account holder" v={data.name} />
                                    <Row k="Contact" v={`${data.email} · ${data.phone}`} />
                                    <hr className="border-border" />
                                    {isQuoteOnly ? (
                                        <Row k="Estimated cost" v="Quote required — no charge until you approve" bold />
                                    ) : (
                                        <Row k="First invoice (est.)" v={fmtMoney(data.monthly_rate * 1.0635)} bold />
                                    )}
                                </div>
                                <label className="flex items-start gap-3 cursor-pointer">
                                    <Checkbox checked={data.accept_terms} onCheckedChange={(v) => set("accept_terms", !!v)} data-testid="accept-terms" />
                                    <div className="text-xs text-muted-foreground leading-relaxed">
                                        {isQuoteOnly
                                            ? `I agree to be contacted by ${company()} at the phone and email above regarding this quote request.`
                                            : `I agree to the ${company()} terms of service, electronic signature, and authorize ${company()} to charge my payment method for recurring service per the selected plan. I understand I can update or pause my service from the customer portal but cancellation must be requested through the office.`}
                                    </div>
                                </label>
                            </div>
                        )}

                        <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
                            <Button variant="outline" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="back-btn">
                                <ArrowLeft className="w-4 h-4 mr-1" />Back
                            </Button>
                            {step < 3 ? (
                                <Button onClick={() => setStep((s) => s + 1)} data-testid="next-btn">
                                    Continue<ArrowRight className="w-4 h-4 ml-1" />
                                </Button>
                            ) : (
                                <Button onClick={submit} disabled={busy || !data.accept_terms} data-testid="submit-signup-btn">
                                    <CreditCard className="w-4 h-4 mr-1" />
                                    {busy ? "Processing…" : (isQuoteOnly ? "Send quote request" : "Create account & pay")}
                                </Button>
                            )}
                        </div>
                    </CardContent>
                </Card>

                <div className="text-center mt-6 text-xs text-muted-foreground">
                    Already a customer? <Link to="/portal/login" className="text-primary hover:underline">Customer portal →</Link>
                </div>
            </div>
        </div>
    );
}

function Row({ k, v, bold }) {
    return (
        <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{k}</span>
            <span className={bold ? "font-heading text-lg font-bold tabular" : "font-medium"}>{v}</span>
        </div>
    );
}
